import { useState, useEffect } from 'react';
import { Text, Button, Badge, Card, CardHeader } from '@fluentui/react-components';
import { BeakerRegular, ArrowClockwiseRegular, PlayRegular } from '@fluentui/react-icons';
import { apiUrl } from '../utils/api';
import { useApiToken } from '../hooks/useApiToken';

interface SimulationScore {
  score: number;
  reason: string;
}

interface SimulationResult {
  persona: string;
  persona_name: string;
  rounds: number;
  conversation_length: number;
  scores: {
    depth: SimulationScore;
    findings: SimulationScore;
    mapping: SimulationScore;
    quality: SimulationScore;
    engagement: SimulationScore;
    satisfaction: SimulationScore;
    overall: number;
    improvements: string[];
  };
  timestamp: string;
  prompt_version?: string;
}

interface SimulationRun {
  id: string;
  timestamp: string;
  promptVersion: string;
  results: SimulationResult[];
  averageOverall: number;
}

const SCORE_COLOUR = (score: number): 'danger' | 'warning' | 'success' => {
  if (score >= 8) return 'success';
  if (score >= 6) return 'warning';
  return 'danger';
};

const CRITERIA = ['depth', 'findings', 'mapping', 'quality', 'engagement', 'satisfaction'] as const;
const CRITERIA_LABELS: Record<string, string> = {
  depth: 'Probing Depth',
  findings: 'Actionable Findings',
  mapping: 'CASE Mapping',
  quality: 'Professional Quality',
  engagement: 'Customer Engagement',
  satisfaction: 'Customer Satisfaction',
};

export function SimulationResultsPage() {
  const getToken = useApiToken();
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<SimulationRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    setLoading(true);
    // Try API first, fall back to static file
    let loaded = false;
    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/simulation/runs'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setRuns(data);
          setSelectedRun(data[data.length - 1]);
          loaded = true;
        }
      }
    } catch { /* fall through to static file */ }

    if (!loaded) {
      try {
        const res = await fetch('/assessment-simulation-results-v2.json');
        if (res.ok) {
          const results: SimulationResult[] = await res.json();
          if (results.length > 0) {
            const run: SimulationRun = {
              id: 'local-1',
              timestamp: results[0]?.timestamp ?? new Date().toISOString(),
              promptVersion: 'v2',
              results,
              averageOverall: results.reduce((s, r) => s + (r.scores?.overall ?? 0), 0) / results.length,
            };
            setRuns([run]);
            setSelectedRun(run);
          }
        }
      } catch { /* no results yet */ }
    }
    setLoading(false);
  };

  const triggerSimulation = async () => {
    setRunning(true);
    try {
      const token = await getToken();
      await fetch(apiUrl('/api/simulation/run'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Poll for completion
      setTimeout(() => { loadRuns(); setRunning(false); }, 5000);
    } catch {
      setRunning(false);
    }
  };

  if (loading) return <div className="loading-container"><Text>Loading simulation results...</Text></div>;

  return (
    <div className="page-root page-root--scrollable simulation-page">
      <div className="page-title-bar">
        <BeakerRegular className="icon-title" />
        <Text size={500} weight="semibold">Assessment Quality Lab</Text>
        <div className="title-bar-actions">
          <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={loadRuns} title="Refresh" />
          <Button appearance="primary" icon={<PlayRegular />} onClick={triggerSimulation} disabled={running}>
            {running ? 'Running...' : 'Run Simulation'}
          </Button>
        </div>
      </div>

      {/* Run history — removed, now using trend chart as selector */}

      {/* Score trend — hero element at top */}
      {runs.length > 0 && (
        <div className="trend-hero">
          <div className="trend-hero-header">
            <Text size={400} weight="semibold">Assessment Quality Trend</Text>
            <Text size={200} className="text-muted">{runs.length} iteration{runs.length !== 1 ? 's' : ''}</Text>
          </div>
          <div className="trend-chart-hero">
            {(() => {
              // Scale bars relative to score range for visible differentiation
              const scores = runs.map(r => Number(r.averageOverall) || 0);
              const minScore = Math.max(Math.min(...scores) - 1, 0);
              const maxScore = Math.min(Math.max(...scores) + 1, 10);
              const range = maxScore - minScore || 1;
              return runs.map((run, i) => {
              const isSelected = run.id === selectedRun?.id;
              const score = Number(run.averageOverall) || 0;
              const prev = i > 0 ? (Number(runs[i - 1].averageOverall) || 0) : score;
              const delta = score - prev;
              const barHeight = Math.max(((score - minScore) / range) * 100, 5);
              return (
                <div
                  key={`${run.id}-${i}`}
                  className={`trend-item ${isSelected ? 'trend-item--selected' : ''}`}
                  onClick={() => setSelectedRun(run)}
                >
                  <div className="trend-bar-wrapper">
                    <div className="trend-bar-fill" data-score={score} style={{ height: `${barHeight}%` }} />
                  </div>
                  <Text size={100} weight="semibold">{score.toFixed(1)}</Text>
                  {i > 0 && Math.abs(delta) >= 0.1 && (
                    <Text size={100} className={delta > 0 ? 'text-success' : 'text-danger'}>
                      {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                    </Text>
                  )}
                </div>
              );
              });
            })()}
          </div>
        </div>
      )}

      {selectedRun && (
        <>
          {/* Score cards */}
          <div className="simulation-summary">
            <Card className="simulation-score-card">
              <CardHeader
                header={<Text weight="semibold">Average Score</Text>}
                description={
                  <Text size={800} weight="bold">
                    <Badge size="large" color={SCORE_COLOUR(selectedRun.averageOverall)}>
                      {selectedRun.averageOverall.toFixed(1)}/10
                    </Badge>
                  </Text>
                }
              />
            </Card>

            {/* Per-criteria averages */}
            {CRITERIA.map(criterion => {
              const avg = selectedRun.results.reduce((s, r) => {
                const c = r.scores?.[criterion];
                return s + (c?.score ?? 0);
              }, 0) / selectedRun.results.length;
              return (
                <Card key={criterion} className="simulation-score-card">
                  <CardHeader
                    header={<Text size={200} weight="semibold">{CRITERIA_LABELS[criterion]}</Text>}
                    description={
                      <Badge size="medium" color={SCORE_COLOUR(avg)}>{avg.toFixed(1)}</Badge>
                    }
                  />
                </Card>
              );
            })}
          </div>

          {/* Per-persona results */}
          <div className="simulation-detail">
            <Text size={400} weight="semibold">Per-Persona Results</Text>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th>Overall</th>
                  {CRITERIA.map(c => <th key={c}>{CRITERIA_LABELS[c]}</th>)}
                </tr>
              </thead>
              <tbody>
                {selectedRun.results.map(r => (
                  <tr key={r.persona}>
                    <td>
                      <Text weight="semibold">{r.persona_name}</Text>
                      <br />
                      <Text size={200} className="text-muted">{r.persona}</Text>
                    </td>
                    <td>
                      <Badge size="small" color={SCORE_COLOUR(r.scores?.overall ?? 0)}>
                        {r.scores?.overall ?? 'N/A'}
                      </Badge>
                    </td>
                    {CRITERIA.map(c => {
                      const score = r.scores?.[c]?.score ?? 0;
                      return (
                        <td key={c}>
                          <Badge size="small" color={SCORE_COLOUR(score)} appearance="outline">{score}</Badge>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Improvement suggestions */}
          <div className="simulation-detail">
            <Text size={400} weight="semibold">Improvement Suggestions</Text>
            {selectedRun.results.map(r => (
              <div key={r.persona} className="improvement-section">
                <Text size={300} weight="semibold">{r.persona_name}</Text>
                <ul className="improvement-list">
                  {(r.scores?.improvements ?? []).slice(0, 5).map((imp, i) => (
                    <li key={i}><Text size={200}>{imp}</Text></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Old trend chart removed — now at top as hero element */}
        </>
      )}

      {runs.length === 0 && (
        <div className="simulation-empty">
          <Text className="text-muted">No simulation results yet. Click "Run Simulation" to start.</Text>
        </div>
      )}
    </div>
  );
}
